import React, { memo } from "react";
import LazyImage from "../UI/LazyImage";
import { useTranslation } from "react-i18next";
import Navigation from "./Navigation";

const Hero: React.FC = memo(() => {
  const { t } = useTranslation("landing");

  return (
    <div className="bg-[#0B0F0E]">
      <Navigation />

      <div className="relative isolate px-6 pt-14 lg:px-8 overflow-hidden">
        {/* Copper glow — top */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
            className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-[#C97A3C] to-[#E89D5C] opacity-30 animate-pulse sm:left-[calc(50%-30rem)] sm:w-288.75"
          />
        </div>

        <div className="mx-auto max-w-7xl py-20 sm:py-28 lg:py-36">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            {/* Left side */}
            <div>
              <div className="hidden sm:mb-8 sm:flex sm:justify-start">
                <div className="relative rounded-full px-3 py-1 text-sm/6 text-[#8A9590] ring-1 ring-white/20 hover:ring-white/30">
                  {t("hero.tagline")}{" "}
                  <a
                    href="javascript:void(0)"
                    className="font-semibold text-[#E89D5C]"
                  >
                    <span aria-hidden="true" className="absolute inset-0" />
                    {t("hero.readMore")} <span aria-hidden="true">&rarr;</span>
                  </a>
                </div>
              </div>

              <div className="text-left">
                <h1 className="text-5xl font-normal tracking-tight text-balance text-white sm:text-7xl">
                  {t("hero.title")}
                </h1>
                <p className="mt-8 text-base font-light text-pretty text-[#8A9590] sm:text-lg/8">
                  {t("hero.description")}
                </p>
                <div className="mt-10 flex items-center justify-start gap-x-6">
                  <a
                    href="javascript:void(0)"
                    className="rounded-lg bg-[#C97A3C] px-8 py-3 text-sm font-medium text-white shadow-xs hover:bg-[#B86B30] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C97A3C] transition-colors"
                  >
                    {t("hero.getStarted")}
                  </a>
                  <a
                    href="javascript:void(0)"
                    className="rounded-lg bg-white/10 border border-white/15 px-8 py-3 text-sm font-normal text-white hover:bg-white/15 transition-colors"
                  >
                    {t("hero.learnMore")} <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right side - spheres */}
            <div className="hidden lg:flex lg:justify-center lg:items-center">
              <div className="relative w-full max-w-lg">
                <div className="aspect-square relative overflow-hidden">
                  <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <LazyImage
                      src="/sphere.png"
                      alt=""
                      className="w-64 h-64 blur-md opacity-30"
                      style={{
                        animation: "heroSphere1 18s ease-in-out infinite",
                      }}
                    />
                  </div>

                  <div className="absolute top-1/4 left-1/4">
                    <LazyImage
                      src="/sphere.png"
                      alt=""
                      className="w-48 h-48 blur-sm opacity-20"
                      style={{
                        animation: "heroSphere2 15s ease-in-out infinite 2s",
                      }}
                    />
                  </div>

                  <div className="absolute bottom-1/3 right-1/4">
                    <LazyImage
                      src="/sphere.png"
                      alt=""
                      className="w-36 h-36 blur-sm opacity-15"
                      style={{
                        animation: "heroSphere3 12s ease-in-out infinite 4s",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes heroSphere1 {
            0%,
            100% {
              transform: translateX(0px) translateY(0px) rotate(0deg);
            }
            25% {
              transform: translateX(-80px) translateY(-60px) rotate(90deg);
            }
            50% {
              transform: translateX(60px) translateY(-80px) rotate(180deg);
            }
            75% {
              transform: translateX(-40px) translateY(70px) rotate(270deg);
            }
          }
          @keyframes heroSphere2 {
            0%,
            100% {
              transform: translateX(0px) translateY(0px) rotate(0deg);
            }
            25% {
              transform: translateX(70px) translateY(50px) rotate(-90deg);
            }
            50% {
              transform: translateX(-90px) translateY(80px) rotate(-180deg);
            }
            75% {
              transform: translateX(50px) translateY(-70px) rotate(-270deg);
            }
          }
          @keyframes heroSphere3 {
            0%,
            100% {
              transform: translateX(0px) translateY(0px) rotate(0deg);
            }
            25% {
              transform: translateX(-60px) translateY(90px) rotate(120deg);
            }
            50% {
              transform: translateX(80px) translateY(-50px) rotate(240deg);
            }
            75% {
              transform: translateX(-70px) translateY(40px) rotate(360deg);
            }
          }
        `}</style>

        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        >
          <div
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
            className="relative left-[calc(50%+3rem)] aspect-1155/678 w-144.5 -translate-x-1/2 bg-linear-to-tr from-[#C97A3C] to-[#E89D5C] opacity-25 animate-pulse sm:left-[calc(50%+36rem)] sm:w-288.75"
          />
        </div>
      </div>
    </div>
  );
});

Hero.displayName = "Hero";
export default Hero;
